import React from 'react';
import styled, { keyframes } from 'styled-components';
import { createRoot } from 'react-dom/client';

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-20px);
    opacity: 0;
  }
`;

const MessageContainer = styled.div`
  position: fixed;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  z-index: 1000;
  
  @media (max-width: 768px) {
    top: 56px;
    padding: 0 16px;
    box-sizing: border-box;
  }
`;

const MessageItem = styled.div`
  min-width: 160px;
  max-width: 90%;
  padding: 10px 24px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-weight: 500;
  animation: ${slideIn} 0.3s ease forwards;
  pointer-events: auto;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(8px);
  word-break: break-word;
  
  &.leaving {
    animation: ${slideOut} 0.3s ease forwards;
  }
`;

const LoadingIcon = styled.div`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;
  vertical-align: middle;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

let messageContainer = null;
let root = null;

const createMessageContainer = (containerId) => {
  if (containerId) {
    let container = document.getElementById(containerId);
    if (!container) {
      console.error('Message container not found:', containerId);
      container = document.createElement('div');
      container.setAttribute('id', containerId);
      document.body.appendChild(container);
    }
    
    if (!root || messageContainer !== container) {
      messageContainer = container;
      if (root) {
        root.unmount();
      }
      root = createRoot(container);
    }
    
    return container;
  }
  
  if (!messageContainer) {
    messageContainer = document.createElement('div');
    messageContainer.setAttribute('id', 'global-message-container');
    document.body.appendChild(messageContainer);
    root = createRoot(messageContainer);
  }
  
  return messageContainer;
};

const Message = {
  show: (content, type = 'info', containerId = null, duration = 6000) => {
    const container = createMessageContainer(containerId);
    const messageId = Date.now();
    let closeTimer = null;

    const handleRemove = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
      }
      
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.classList.add('leaving');
        setTimeout(() => {
          if (root) {
            root.render(<MessageContainer />);
          }
        }, 300);
      }
    };

    const MessageComponent = () => (
      <MessageContainer>
        <MessageItem id={`message-${messageId}`} className={type}>
          {type === 'loading' && <LoadingIcon />}
          {content}
        </MessageItem>
      </MessageContainer>
    );

    root.render(<MessageComponent />);

    if (duration > 0) {
      closeTimer = setTimeout(handleRemove, duration);
    }

    return messageId;
  },

  success: (content, containerId = null, duration) => {
    return Message.show(content, 'success', containerId, duration);
  },

  error: (content, containerId = null, duration) => {
    return Message.show(content, 'error', containerId, duration);
  },

  info: (content, containerId = null, duration) => {
    return Message.show(content, 'info', containerId, duration);
  },

  warning: (content, containerId = null, duration) => {
    return Message.show(content, 'warning', containerId, duration);
  },

  loading: (content, containerId = null, duration) => {
    return Message.show(content, 'loading', containerId, duration);
  },

  destroy: (messageId) => {
    if (messageId) {
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.classList.add('leaving');
        setTimeout(() => {
          if (root) {
            root.render(<MessageContainer />);
          }
        }, 300);
      }
    } else {
      if (messageContainer && root) {
        root.render(<MessageContainer />);
      }
    }
  }
};

export default Message; 